import colors from '@/styles/colors'
import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Tabs, { tabsClasses } from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { styled } from '@mui/system'
import { Button, IconButton, Typography, withStyles } from '@mui/material'
import {
  ArrowBackIosNewRounded as ArrowBack,
  ArrowForwardIosRounded as ArrowForward,
} from '@mui/icons-material'
// import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { RBox } from '@/pages/__styled'

interface ScrollableCapsulesTabBarProps {
  titles: String[]
  selectedIndex: number
  showScrollButtons?: boolean
  onSelectAtIndex: (index: number) => void
}

const CustomTab = styled(Box)(({ theme }) => ({
  textTransform: 'capitalize',
  backgroundColor: '#f8fafc',
  cursor: 'pointer',
  borderRadius: 12,
  display: 'flex',
  minWidth: '80px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 24px',
  color: '#64748b',
  border: '2px solid transparent',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',

  '&:hover': {
    backgroundColor: '#ffffff',
    borderColor: 'rgba(99, 102, 241, 0.2)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.12)',
    color: '#6366f1',
  },

  '&[aria-selected = true]': {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    border: '2px solid transparent',
    boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
    transform: 'translateY(-2px)',

    '&:hover': {
      background: 'linear-gradient(135deg, #5558e3 0%, #7c4de8 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
    },
  },
}))

export const CapsuleView = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: colors.themeBlack,
  borderColor: theme.palette.brand.light,
  border: `1px solid #E7EDF3`,
  fontSize: 13,
  fontWeight: '500',
  borderRadius: 50,
  alignItems: 'center',
  padding: '5px 15px',
  '&[aria-selected = true]': {
    border: `1px solid ${colors.meshdomBlue}`,
    backgroundColor: colors.meshdomBlueLight,
    color: colors.meshdomBlue,
  },
  '&:hover': {
    border: `1px solid ${colors.meshdomBlue}`,
    backgroundColor: colors.meshdomBlueLight,
    color: colors.meshdomBlue,
  },
}))

export const ScrollableCapsulesTabBar = ({
  titles,
  selectedIndex,
  onSelectAtIndex,
  showScrollButtons = true,
}: ScrollableCapsulesTabBarProps) => {
  const [scrollPosition, setScrollPosition] = useState(0)
  const scrollRef = React.useRef<HTMLDivElement>(null)

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { current: element } = scrollRef
      const scrollAmount = 200
      const newScrollPosition =
        direction === 'left' ? element.scrollLeft - scrollAmount : element.scrollLeft + scrollAmount

      element.scrollTo({ left: newScrollPosition, behavior: 'smooth' })
      setScrollPosition(newScrollPosition)
    }
  }

  const backArrow = useCallback(() => {
    const disabled = scrollPosition <= 0
    return (
      <RBox
        onClick={() => handleScroll('left')}
        sx={{
          border: '2px solid rgba(99, 102, 241, 0.2)',
          borderRadius: '12px',
          width: '40px',
          height: '40px',
          zIndex: 10,
          left: 0,
          position: 'absolute',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 2px 8px rgba(99, 102, 241, 0.08)',
          '&:hover': disabled
            ? {}
            : {
                backgroundColor: '#6366f1',
                borderColor: '#6366f1',
                transform: 'translateX(-2px)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                '& svg': {
                  color: 'white',
                },
              },
        }}
      >
        <ArrowBack
          fontSize='small'
          sx={{ color: '#6366f1', opacity: disabled ? 0.4 : 1, transition: 'all 0.2s' }}
        />
      </RBox>
    )
  }, [scrollPosition])

  //

  const forwardArrow = useCallback(() => {
    let disabled = false
    if (scrollRef.current) {
      const { current: element } = scrollRef
      disabled = element.scrollLeft + element.clientWidth >= element.scrollWidth
    }

    return (
      <RBox
        onClick={() => handleScroll('right')}
        sx={{
          zIndex: 10,
          border: '2px solid rgba(99, 102, 241, 0.2)',
          borderRadius: '12px',
          width: '40px',
          height: '40px',
          backgroundColor: 'white',
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 2px 8px rgba(99, 102, 241, 0.08)',
          '&:hover': disabled
            ? {}
            : {
                backgroundColor: '#6366f1',
                borderColor: '#6366f1',
                transform: 'translateX(2px)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                '& svg': {
                  color: 'white',
                },
              },
        }}
      >
        <ArrowForward
          fontSize='small'
          sx={{ color: '#6366f1', opacity: disabled ? 0.4 : 1, transition: 'all 0.2s' }}
        />
      </RBox>
    )
  }, [scrollPosition])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        my: 0,
        position: 'relative',
        minHeight: '56px',
      }}
    >
      {showScrollButtons && backArrow()}
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: '12px',
          overflow: 'hidden',
          px: showScrollButtons ? '20px' : '0px',
          position: 'absolute',
          left: showScrollButtons ? 32 : 0,
          right: showScrollButtons ? 32 : 0,
        }}
      >
        {titles.map((e, i) => (
          <CustomTab
            key={`${i}`}
            aria-selected={selectedIndex == i}
            onClick={() => {
              onSelectAtIndex(i)
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                flex: 1,
                color: 'inherit',
                textAlign: 'center',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '-0.01em',
              }}
            >{`${e}`}</Typography>
          </CustomTab>
        ))}
      </Box>
      {showScrollButtons && forwardArrow()}
    </Box>
  )
}
